import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar, Header } from './Sidebar';
import { Toaster } from 'react-hot-toast';
import { cn } from '../../lib/utils';

const APP_NAME = 'Tailody Hosting';
const SIDEBAR_COLLAPSED_KEY = 'tailody-sidebar-collapsed';

// 인증 페이지 경로별 메뉴 이름 매핑
const AUTH_ROUTE_TITLES: Record<string, string> = {
    '/login': '로그인',
    '/register': '회원가입',
};

interface Group {
    id: number;
    name?: string;
}

interface MainLayoutProps {
    children: React.ReactNode;
    userName?: string;
    userImage?: string;
    onLogout?: () => void;
    groups?: Group[];
    selectedGroupId?: number;
    onGroupChange?: (groupId: number) => void;
}

export function MainLayout({ children, userName, userImage, onLogout, groups, selectedGroupId, onGroupChange }: MainLayoutProps) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        // localStorage에서 초기값 로드
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
            return saved === 'true';
        }
        return false;
    });

    // collapsed 상태 변경 시 localStorage에 저장
    const handleCollapsedChange = (collapsed: boolean) => {
        setSidebarCollapsed(collapsed);
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    };

    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
            <Sidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                onLogout={onLogout}
                isCollapsed={sidebarCollapsed}
                onCollapsedChange={handleCollapsedChange}
            />

            <div
                className={cn(
                    "transition-all duration-300",
                    sidebarCollapsed ? "lg:pl-16" : "lg:pl-64"
                )}
                style={{ '--sidebar-width': sidebarCollapsed ? '4rem' : '16rem' } as React.CSSProperties}
            >
                <Header
                    onMenuClick={() => setSidebarOpen(!sidebarOpen)}
                    userName={userName}
                    userImage={userImage}
                    groups={groups}
                    selectedGroupId={selectedGroupId}
                    onGroupChange={onGroupChange}
                />

                <main className="p-4 lg:p-6 overflow-hidden pt-20 lg:pt-[88px]">{children}</main>
            </div>
            <Toaster
                containerClassName="!inset-[64px] !w-full !left-0"
                toastOptions={{
                    duration: 2000,
                    className:
                        "!w-full !m-0 px-4 py-2 !bg-black/70 !rounded-xl !backdrop-blur-sm",
                }}
            />
        </div>
    );
}

interface AuthLayoutProps {
    children: React.ReactNode;
}

export function AuthLayout({ children }: AuthLayoutProps) {
    const location = useLocation();

    // 인증 페이지 동적 타이틀 설정
    useEffect(() => {
        const menuName = AUTH_ROUTE_TITLES[location.pathname] || '로그인';
        document.title = `${APP_NAME} | ${menuName}`;
    }, [location.pathname]);

    return (
        <div className="flex min-h-screen">
            <div className="hidden w-1/2 bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 lg:flex lg:flex-col lg:justify-center lg:px-12">
                <div className="text-white">
                    <div className="mb-6 flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 text-2xl font-bold backdrop-blur">
                            T
                        </div>
                        <span className="text-3xl font-bold">Tailody</span>
                    </div>
                    <h1 className="mb-4 text-4xl font-bold leading-tight">
                        반려동물을 위한<br />
                        프리미엄 위탁케어
                    </h1>
                    <p className="text-lg text-white/80">
                        IoT 기술로 실시간 모니터링, 안전한 케어 서비스를 경험하세요.
                    </p>
                </div>
                <div className="absolute bottom-0 left-0 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
                <div className="absolute top-1/4 right-12 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
            </div>

            <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-12 bg-white dark:bg-neutral-950">
                <div className="mx-auto w-full max-w-md">
                    {children}
                </div>
            </div>
        </div>
    );
}
